import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

const fcFmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// Currencies are stored as the full pair (USDINR) and shown as USD/INR.
const pairLabel = (c) =>
  c && c.length === 6 ? `${c.slice(0, 3)}/${c.slice(3)}` : (c || '—');

const serverMessage = (err, fallback) => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d.title === 'string') return d.title;
  return fallback;
};

function InvoicesPage({ token }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditInvoice, setPendingEditInvoice] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [currencies, setCurrencies] = useState([]);   // v22 S2
  const [pendingOrders, setPendingOrders] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');

  // ---- Phase 1: allocations read back for the invoice being edited ----
  const [allocationData, setAllocationData] = useState(null);
  const [allocationsLoading, setAllocationsLoading] = useState(false);
  const [allocationsError, setAllocationsError] = useState('');
  const [editAllocations, setEditAllocations] = useState({});

  const [form, setForm] = useState({
    customerCode: '', currency: '', invoiceNo: '', invoiceDate: '',
    dueDate: '', customsRate: '', shippingStatus: 'PENDING',
    notes: ''
  });

  useEffect(() => {
    loadInvoices();
    loadCustomers();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/invoices`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setInvoices(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/invoices/customers`, {
        headers: { authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // v22 S2: orders are now listed for one customer AND one currency.
  // An invoice holds a single currency - everything downstream (forwards,
  // PCFC, EEFC) matches on it.
  const loadPendingOrders = async (customerCode, currency) => {
    setAllocations({});
    if (!customerCode || !currency) { setPendingOrders([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/invoices/pending-orders/${encodeURIComponent(customerCode)}`,
        { params: { currency },
          headers: { authorization: `Bearer ${token}` } }
      );
      // belt and braces - never show an order of another currency
      setPendingOrders((res.data || []).filter(o => (o.currency || '') === currency));
    } catch (err) {
      console.error(err);
      setPendingOrders([]);
    }
  };

  // Which currencies this customer actually has orders in. If there is only
  // one, it is chosen straight away so nothing extra is asked of the user.
  const loadCurrencies = async (customerCode) => {
    setCurrencies([]);
    setPendingOrders([]);
    setAllocations({});
    if (!customerCode) return '';
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/invoices/pending-currencies/${encodeURIComponent(customerCode)}`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setCurrencies(res.data || []);
      if ((res.data || []).length === 1) {
        loadPendingOrders(customerCode, res.data[0]);
        return res.data[0];
      }
    } catch (err) {
      console.error(err);
    }
    return '';
  };

  // ---- Phase 1: fetch which orders this invoice draws from ----
  const loadAllocations = async (invoiceId) => {
    setAllocationsLoading(true);
    setAllocationsError('');
    setAllocationData(null);
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/invoices/${invoiceId}/allocations`,
        { headers: { authorization: `Bearer ${token}` } }
      );
      setAllocationData(res.data);
      if (res.data.currency) setForm(f => ({ ...f, currency: res.data.currency }));

      // seed the input boxes with what the invoice holds today
      const seed = {};
      (res.data.orders || []).forEach(o => {
        if (o.onThisInvoice) seed[o.orderId] = String(o.allocatedFc);
      });
      setEditAllocations(seed);
    } catch (err) {
      console.error(err);
      setAllocationsError('Could not load the order allocations for this invoice.');
    } finally {
      setAllocationsLoading(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };

    if (name === 'customerCode') {
      updated.currency = '';
      setForm(updated);
      const only = await loadCurrencies(value);
      if (only) setForm(f => ({ ...f, currency: only }));
      return;
    }

    setForm(updated);

    if (name === 'currency') {
      loadPendingOrders(updated.customerCode, value);
    }
  };

  const handleAllocationChange = (orderId, value) => {
    setAllocations({ ...allocations, [orderId]: value });
  };

  const calculateWeightedAvg = () => {
    let totalFc = 0;
    let weightedSum = 0;
    pendingOrders.forEach(order => {
      const alloc = parseFloat(allocations[order.orderId] || 0);
      if (alloc > 0) {
        totalFc += alloc;
        weightedSum += alloc * parseFloat(order.costRate);
      }
    });
    if (totalFc === 0) return { totalFc: 0, weightedAvg: 0 };
    return { totalFc, weightedAvg: weightedSum / totalFc };
  };

  const resetForm = () => {
    setForm({
      customerCode: '', currency: '', invoiceNo: '', invoiceDate: '',
      dueDate: '', customsRate: '', shippingStatus: 'PENDING',
      notes: ''
    });
    setAllocations({});
    setCurrencies([]);
    setPendingOrders([]);
    setEditingInvoice(null);
    setAllocationData(null);
    setEditAllocations({});
    setAllocationsError('');
    setAllocationsLoading(false);
    setShowForm(false);
  };

  // Step 1 — user clicks Edit — show warning popup first
  const handleEditClick = (invoice) => {
    if (invoice.status === 'CANCELLED' || invoice.status === 'CLOSED') {
      alert('This invoice cannot be edited.');
      return;
    }
    setPendingEditInvoice(invoice);
    setShowEditWarning(true);
  };

  // Step 2 — user confirms warning — open edit form
  const handleEditConfirm = () => {
    const invoice = pendingEditInvoice;
    setShowEditWarning(false);
    setPendingEditInvoice(null);
    setForm({
      customerCode:   invoice.customerCode || '',
      currency:       invoice.currency || '',
      invoiceNo:      invoice.invoiceNo || '',
      invoiceDate:    invoice.invoiceDate || '',
      dueDate:        invoice.dueDate || '',
      customsRate:    invoice.customsRate || '',
      shippingStatus: invoice.shippingStatus || 'PENDING',
      notes:          typeof invoice.notes === 'string' ? invoice.notes : ''
    });
    setEditingInvoice(invoice);
    setShowForm(true);
    loadAllocations(invoice.invoiceId);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!form.invoiceNo)   { alert('Please enter invoice number'); return; }
    if (!form.invoiceDate) { alert('Please enter invoice date'); return; }
    if (!form.dueDate)     { alert('Please enter due date'); return; }
    if (!form.customsRate) { alert('Please enter customs rate'); return; }

    // EDIT mode
    if (editingInvoice) {
      const payload = {
        invoiceNo:      form.invoiceNo,
        invoiceDate:    form.invoiceDate,
        dueDate:        form.dueDate,
        customsRate:    parseFloat(form.customsRate),
        shippingStatus: form.shippingStatus,
        notes:          form.notes || null
      };

      // Only send an allocation list when reallocation is actually
      // allowed. Leaving it off entirely tells the server to touch
      // nothing but the cosmetic fields.
      if (canReallocate) {
        const list = [];
        for (const o of allocationData.orders) {
          const v = parseFloat(editAllocations[o.orderId] || 0);
          if (!(v > 0)) continue;
          if (v > parseFloat(o.availableFc) + 0.01) {
            alert(`Order ${o.orderNo}: ${fcFmt(v)} is more than the `
                + `${fcFmt(o.availableFc)} available on it.`);
            return;
          }
          list.push({ orderId: o.orderId, allocatedFc: v });
        }
        if (list.length === 0) {
          alert('At least one order must have an amount allocated to it.');
          return;
        }
        payload.allocations = list;
      }

      try {
        await axios.put(
          `${CONFIG.API}/api/bills/invoices/${editingInvoice.invoiceId}`,
          payload,
          { headers: { authorization: `Bearer ${token}` } }
        );
        resetForm();
        loadInvoices();
        loadCustomers();
      } catch (err) {
        alert(serverMessage(err, 'Error updating invoice. Please try again.'));
      }
      return;
    }

    // NEW mode
    if (!form.customerCode) { alert('Please select a customer'); return; }
    if (!form.currency)     { alert('Please select a currency'); return; }

    const allocationList = pendingOrders
      .filter(o => parseFloat(allocations[o.orderId] || 0) > 0)
      .map(o => ({
        orderId:     o.orderId,
        allocatedFc: parseFloat(allocations[o.orderId])
      }));

    if (allocationList.length === 0) {
      alert('Please allocate FC amount to at least one order');
      return;
    }

    for (const o of pendingOrders) {
      const alloc = parseFloat(allocations[o.orderId] || 0);
      if (alloc > parseFloat(o.fcBalance)) {
        alert(`Allocated FC for order ${o.orderNo} exceeds available balance of ${o.fcBalance}`);
        return;
      }
    }

    try {
      await axios.post(`${CONFIG.API}/api/bills/invoices`, {
        customerCode:   form.customerCode,
        currency:       form.currency,
        invoiceNo:      form.invoiceNo,
        invoiceDate:    form.invoiceDate,
        dueDate:        form.dueDate,
        customsRate:    parseFloat(form.customsRate),
        shippingStatus: form.shippingStatus,
        notes:          form.notes || null,
        allocations:    allocationList
      }, {
        headers: { authorization: `Bearer ${token}` }
      });

      resetForm();
      loadInvoices();
      loadCustomers();
    } catch (err) {
      alert(serverMessage(err, 'Error saving invoice. Please check all fields.'));
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm(
      'Cancel this invoice?\n\nThis will restore the FC balance back to the original orders.'
    )) return;
    try {
      await axios.put(
        `${CONFIG.API}/api/bills/invoices/${invoiceId}/cancel`, {},
        { headers: { authorization: `Bearer ${token}` } }
      );
      loadInvoices();
      loadCustomers();
    } catch (err) {
      alert(serverMessage(err, 'Cannot cancel — discounting or realisation already recorded.'));
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'OPEN':         'badge-open',
      'PART_SETTLED': 'badge-partial',
      'CLOSED':       'badge-done',
      'CANCELLED':    'badge-cancelled'
    };
    return map[status] || 'badge-open';
  };

  const getShippingBadge = (s) => s === 'SHIPPED' ? 'badge-done' : 'badge-partial';

  const { totalFc, weightedAvg } = calculateWeightedAvg();

  // every order this invoice could draw on: the ones it already uses,
  // plus the customer's other orders that still have a balance
  const editOrders    = allocationData ? allocationData.orders : [];
  const canReallocate = !!(allocationData && allocationData.canReallocate);

  // live totals as the amounts are typed
  let editTotal = 0, editWeighted = 0;
  editOrders.forEach(o => {
    const v = parseFloat(editAllocations[o.orderId] || 0);
    if (v > 0) { editTotal += v; editWeighted += v * parseFloat(o.costRate || 0); }
  });
  const editWavg = editTotal > 0 ? editWeighted / editTotal : 0;

  const filteredInvoices = invoices.filter(inv => {
    const matchStatus   = filterStatus   === '' || inv.status === filterStatus;
    const matchCustomer = filterCustomer === '' ||
      inv.customerCode.toLowerCase().includes(filterCustomer.toLowerCase());
    return matchStatus && matchCustomer;
  });

  return (
    <div className="content">

      {/* Edit Warning Popup */}
      {showEditWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px',
            maxWidth: '460px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#1E293B', marginBottom: '12px' }}>
              ⚠️ Edit Invoice
            </div>
            <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '20px' }}>
              You can edit the following details:
              <ul style={{ marginTop: '8px', marginBottom: '8px', paddingLeft: '18px' }}>
                <li>Invoice No</li>
                <li>Invoice Date</li>
                <li>Due Date</li>
                <li>Customs Rate</li>
                <li>Shipping Status</li>
                <li>Notes</li>
              </ul>
              <strong>You can also change the order allocations.</strong> The
              orders this invoice draws from are shown in the form, along with
              any other open orders for this customer. Change an amount, set one
              to zero to drop it, or allocate against a different order — the
              invoice total recalculates itself.
              <br /><br />
              Allocations are locked once discounting or realisation has been
              recorded against the invoice.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                className="btn-secondary"
                onClick={() => { setShowEditWarning(false); setPendingEditInvoice(null); }}>
                Close
              </button>
              <button className="btn-primary" onClick={handleEditConfirm}>
                Yes, Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total invoices</div>
          <div className="metric-value val-blue">{invoices.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-green">
            {invoices.filter(i => i.status === 'OPEN').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Part settled</div>
          <div className="metric-value val-amber">
            {invoices.filter(i => i.status === 'PART_SETTLED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Closed</div>
          <div className="metric-value val-blue">
            {invoices.filter(i => i.status === 'CLOSED').length}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="section-header">
        <div className="section-title">All invoices</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New invoice'}
        </button>
      </div>

      {/* Form — New or Edit */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '720px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingInvoice ? '✏️ Edit invoice' : '📋 New invoice'}
          </div>

          {/* Row 1 — Customer + Currency + Invoice no */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '6px' }}>
            <div className="form-field">
              <label>Customer code</label>
              {editingInvoice ? (
                <input value={form.customerCode} disabled
                  style={{ background: '#F1F5F9', color: '#94A3B8' }} />
              ) : (
                <select name="customerCode" value={form.customerCode} onChange={handleChange}>
                  <option value="">— Select customer —</option>
                  {customers.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-field">
              <label>Currency</label>
              {editingInvoice ? (
                <input value={pairLabel(form.currency)} disabled
                  style={{ background: '#F1F5F9', color: '#94A3B8' }} />
              ) : (
                <select name="currency" value={form.currency} onChange={handleChange}
                  disabled={!form.customerCode || currencies.length === 0}>
                  <option value="">
                    {!form.customerCode ? '— Select customer first —' : '— Select currency —'}
                  </option>
                  {currencies.map(c => (
                    <option key={c} value={c}>{pairLabel(c)}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-field">
              <label>Invoice no</label>
              <input name="invoiceNo" value={form.invoiceNo}
                onChange={handleChange} placeholder="Bank / customs reference" />
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '12px' }}>
            {editingInvoice
              ? 'Customer and currency are locked — the order list below depends on them.'
              : 'One invoice uses orders of one currency only. Forwards, PCFC and EEFC all match on it.'}
          </div>

          {/* Row 2 — Invoice date, Due date, Customs rate */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Invoice date (BL date)</label>
              <input type="date" name="invoiceDate" value={form.invoiceDate}
                onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Due date</label>
              <input type="date" name="dueDate" value={form.dueDate}
                onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Customs rate</label>
              <input type="number" name="customsRate" value={form.customsRate}
                onChange={handleChange} placeholder="e.g. 84.50" />
            </div>
          </div>

          {/* Row 3 — Shipping status + Total invoice value */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div className="form-field">
              <label>Shipping status</label>
              <select name="shippingStatus" value={form.shippingStatus} onChange={handleChange}>
                <option value="PENDING">Pending</option>
                <option value="SHIPPED">Shipped</option>
              </select>
            </div>
            <div className="form-field">
              <label>Total invoice value (FC)</label>
              {editingInvoice ? (
                <input
                  value={fcFmt(editingInvoice.fcAmount)}
                  disabled
                  style={{ background: '#F1F5F9', color: '#1E293B', fontWeight: 600 }} />
              ) : (
                <input
                  value={totalFc > 0 ? fcFmt(totalFc) : ''}
                  disabled
                  placeholder="Set by the order allocation below"
                  style={{ background: '#F1F5F9', color: '#1E293B', fontWeight: 600 }} />
              )}
            </div>
          </div>

          {/* Order allocation table — NEW invoice only (editable) */}
          {!editingInvoice && pendingOrders.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Allocate FC from pending {pairLabel(form.currency)} orders
              </div>
              <table className="data-table" style={{ marginBottom: '8px' }}>
                <thead>
                  <tr>
                    <th>Order no</th>
                    <th>Date</th>
                    <th>Cost rate</th>
                    <th>FC balance</th>
                    <th>Allocate FC</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOrders.map(order => (
                    <tr key={order.orderId}>
                      <td>{order.orderNo}</td>
                      <td>{order.orderDate}</td>
                      <td>{Number(order.costRate).toFixed(4)}</td>
                      <td>{Number(order.fcBalance).toLocaleString()}</td>
                      <td>
                        <input
                          type="number"
                          value={allocations[order.orderId] || ''}
                          onChange={e => handleAllocationChange(order.orderId, e.target.value)}
                          placeholder="0"
                          max={order.fcBalance}
                          style={{
                            width: '110px', padding: '4px 8px',
                            border: '1px solid #CBD5E1', borderRadius: '4px',
                            fontSize: '13px'
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalFc > 0 && (
                <div style={{
                  background: '#F0FDF4', border: '1px solid #BBF7D0',
                  borderRadius: '6px', padding: '10px 14px',
                  display: 'flex', gap: '32px', fontSize: '13px'
                }}>
                  <span><strong>Total FC allocated:</strong> {Number(totalFc).toLocaleString()}</span>
                  <span><strong>Weighted avg cost rate:</strong> {weightedAvg.toFixed(4)}</span>
                </div>
              )}
            </div>
          )}

          {/* Helper messages — new invoice */}
          {!editingInvoice && form.customerCode && currencies.length === 0 && (
            <div style={{
              background: '#FEF9C3', border: '1px solid #FDE68A',
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', color: '#92400E', marginBottom: '16px'
            }}>
              No pending orders found for this customer.
            </div>
          )}
          {!editingInvoice && form.customerCode && currencies.length > 1 && !form.currency && (
            <div style={{
              background: '#FEF9C3', border: '1px solid #FDE68A',
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', color: '#92400E', marginBottom: '16px'
            }}>
              This customer has pending orders in more than one currency. Choose a currency —
              one invoice can only use orders of one currency.
            </div>
          )}
          {!editingInvoice && form.currency && currencies.length > 0 && pendingOrders.length === 0 && (
            <div style={{
              background: '#FEF9C3', border: '1px solid #FDE68A',
              borderRadius: '6px', padding: '10px 14px',
              fontSize: '13px', color: '#92400E', marginBottom: '16px'
            }}>
              No pending {pairLabel(form.currency)} orders found for this customer.
            </div>
          )}

          {/* ---- Orders used by this invoice — EDIT mode ---- */}
          {editingInvoice && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Orders allocated to this invoice
              </div>

              {allocationsLoading && (
                <div style={{ fontSize: '13px', color: '#64748B', padding: '8px 0' }}>
                  Loading order allocations...
                </div>
              )}

              {allocationsError && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FCA5A5',
                  borderRadius: '6px', padding: '10px 14px',
                  fontSize: '13px', color: '#B91C1C'
                }}>
                  {allocationsError}
                </div>
              )}

              {!allocationsLoading && !allocationsError && allocationData && (
                <>
                  {!canReallocate && allocationData.lockReason && (
                    <div style={{
                      background: '#FEF9C3', border: '1px solid #FDE68A',
                      borderRadius: '6px', padding: '10px 14px',
                      fontSize: '13px', color: '#92400E', marginBottom: '8px'
                    }}>
                      🔒 {allocationData.lockReason}
                    </div>
                  )}

                  {editOrders.length === 0 ? (
                    <div style={{
                      background: '#FEF9C3', border: '1px solid #FDE68A',
                      borderRadius: '6px', padding: '10px 14px',
                      fontSize: '13px', color: '#92400E'
                    }}>
                      No order allocations are recorded against this invoice.
                    </div>
                  ) : (
                    <>
                      <table className="data-table" style={{ marginBottom: '8px' }}>
                        <thead>
                          <tr>
                            <th>Order no</th>
                            <th>Date</th>
                            <th>Cost rate</th>
                            <th>Available FC</th>
                            <th>Allocate FC</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editOrders.map(o => (
                            <tr key={o.orderId}>
                              <td>
                                {o.orderNo}
                                {!o.onThisInvoice && (
                                  <span style={{
                                    marginLeft: '6px', fontSize: '11px',
                                    color: '#64748B', fontStyle: 'italic'
                                  }}>
                                    not yet used
                                  </span>
                                )}
                              </td>
                              <td>{o.orderDate}</td>
                              <td>{Number(o.costRate).toFixed(4)}</td>
                              <td>{fcFmt(o.availableFc)}</td>
                              <td>
                                {canReallocate ? (
                                  <input
                                    type="number"
                                    value={editAllocations[o.orderId] || ''}
                                    onChange={e => setEditAllocations({
                                      ...editAllocations,
                                      [o.orderId]: e.target.value
                                    })}
                                    placeholder="0"
                                    max={o.availableFc}
                                    style={{
                                      width: '110px', padding: '4px 8px',
                                      border: '1px solid #CBD5E1', borderRadius: '4px',
                                      fontSize: '13px'
                                    }}
                                  />
                                ) : (
                                  <span style={{ fontWeight: 600 }}>{fcFmt(o.allocatedFc)}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div style={{
                        background: '#F0FDF4', border: '1px solid #BBF7D0',
                        borderRadius: '6px', padding: '10px 14px',
                        display: 'flex', gap: '32px', fontSize: '13px', flexWrap: 'wrap'
                      }}>
                        <span>
                          <strong>Total invoice value:</strong> {fcFmt(editTotal)}
                        </span>
                        <span>
                          <strong>Weighted avg cost rate:</strong> {editWavg.toFixed(4)}
                        </span>
                        {canReallocate && Math.abs(editTotal - allocationData.fcAmount) > 0.01 && (
                          <span style={{ color: '#B45309' }}>
                            was {fcFmt(allocationData.fcAmount)}
                          </span>
                        )}
                      </div>

                      {canReallocate && (
                        <div style={{ fontSize: '12px', color: '#64748B', marginTop: '6px' }}>
                          Set an amount to 0 to remove that order from this invoice.
                          Available FC already includes what this invoice holds.
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows={3}
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit}>
              {editingInvoice ? '✓ Update invoice' : '✓ Save invoice'}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }}
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="PART_SETTLED">Part settled</option>
          <option value="CLOSED">Closed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        {(filterStatus !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterStatus(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredInvoices.length} of {invoices.length} invoices
        </span>
      </div>

      {/* Invoices table */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredInvoices.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No invoices found. Try changing the filters.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice no</th>
                <th>Customer</th>
                <th>Currency</th>
                <th>Invoice date</th>
                <th>Due date</th>
                <th>Invoice amount</th>
                <th>Customs rate</th>
                <th>Wtd cost rate</th>
                <th>FC open</th>
                <th>Shipping</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(inv => (
                <tr key={inv.invoiceId}>
                  <td>{inv.invoiceNo}</td>
                  <td>{inv.customerCode}</td>
                  <td>{pairLabel(inv.currency)}</td>
                  <td>{inv.invoiceDate}</td>
                  <td>{inv.dueDate}</td>
                  <td>{Number(inv.fcAmount).toLocaleString()}</td>
                  <td>{Number(inv.customsRate).toFixed(4)}</td>
                  <td>{Number(inv.weightedAvgCostRate).toFixed(4)}</td>
                  <td>{Number(inv.fcOpen).toLocaleString()}</td>
                  <td>
                    <span className={`badge ${getShippingBadge(inv.shippingStatus)}`}>
                      {inv.shippingStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {inv.status !== 'CANCELLED' && inv.status !== 'CLOSED' && (
                      <button
                        className="btn-secondary"
                        onClick={() => handleEditClick(inv)}
                        style={{ marginRight: '6px' }}>
                        Edit
                      </button>
                    )}
                    {inv.status === 'OPEN' && (
                      <button
                        className="btn-danger"
                        onClick={() => handleCancelInvoice(inv.invoiceId)}>
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}

export default InvoicesPage;